import { RefreshCw, Search, Zap, AlertTriangle, CheckCircle, Brain } from "lucide-react"
import { useSystemStore } from "@/store/systemStore"

export default function DiagnosisPanel() {
  const snapshot = useSystemStore(s => s.snapshot)
  const diagnosis = useSystemStore(s => s.diagnosis)
  const diagnosing = useSystemStore(s => s.diagnosing)
  const autopilot = useSystemStore(s => s.autopilot)
  const authority = useSystemStore(s => s.authority)
  const runDiagnosis = useSystemStore(s => s.runDiagnosis)
  const dismissDiagnosis = useSystemStore(s => s.dismissDiagnosis)
  const doToggleAutopilot = useSystemStore(s => s.doToggleAutopilot)
  const doVeto = useSystemStore(s => s.doVeto)

  // Quick analysis from current metrics (always-on summary)
  const quickAnalysis = (() => {
    if (!snapshot) return null
    const issues: string[] = []
    let severity: "ok" | "watch" | "critical" = "ok"

    if (snapshot.alerts_critical > 0) {
      issues.push(`${snapshot.alerts_critical} critical alert${snapshot.alerts_critical > 1 ? "s" : ""} active`)
      severity = "critical"
    }
    if (snapshot.queue_depth > 30) {
      issues.push(`Queue backlog: ${snapshot.queue_depth} jobs waiting`)
      if (severity === "ok") severity = "watch"
    }
    if ((snapshot.gpu_pct ?? 0) > 90) {
      issues.push(`GPU at ${snapshot.gpu_pct}% — approaching capacity`)
      if (severity === "ok") severity = "watch"
    }
    if (!snapshot.worker_alive) {
      issues.push("Worker is not alive")
      severity = "critical"
    }
    if (snapshot.gpu_workers_ready === 0 && snapshot.gpu_workers_alive > 0) {
      issues.push("Workers alive but none ready — possible engine issue")
      if (severity === "ok") severity = "watch"
    }
    if ((snapshot.disk_pct ?? 0) > 85) {
      issues.push(`Disk usage high: ${Math.round(snapshot.disk_pct!)}%`)
      if (severity === "ok") severity = "watch"
    }
    if (snapshot.throughput_jobs_per_min === 0 && snapshot.queue_depth > 0) {
      issues.push("Queue has jobs but throughput is zero — processing stalled")
      severity = "critical"
    }
    if ((snapshot as any).stalled_jobs > 0) {
      issues.push(`${(snapshot as any).stalled_jobs} job(s) stalled at high progress — likely stuck`)
      severity = "critical"
    }
    if ((snapshot as any).slow_jobs > 0) {
      issues.push(`${(snapshot as any).slow_jobs} job(s) exceeding 3-minute SLO target`)
      if (severity === "ok") severity = "watch"
    }
    if ((snapshot as any).longest_active_s > 300) {
      const mins = ((snapshot as any).longest_active_s / 60).toFixed(1)
      issues.push(`Longest active job running for ${mins}min`)
      if (severity === "ok") severity = "watch"
    }
    if (snapshot.throughput_avg_s > 180 && snapshot.throughput_avg_s > 0) {
      issues.push(`Avg processing time ${snapshot.throughput_avg_s.toFixed(0)}s — exceeds 180s SLO`)
      if (severity === "ok") severity = "watch"
    }

    return { issues, severity }
  })()

  const severityColors = {
    ok: { bg: "bg-emerald-500/10", border: "border-emerald-500/20", text: "text-emerald-400", label: "System Healthy" },
    watch: { bg: "bg-amber-500/10", border: "border-amber-500/20", text: "text-amber-400", label: "Needs Attention" },
    critical: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", label: "Action Required" },
  }

  const pendingActions = authority?.pending_actions ?? []

  return (
    <div className="flex flex-col gap-3 h-full overflow-y-auto p-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Brain size={14} className="text-indigo-400" />
        <span className="text-xs font-medium text-gray-200 uppercase tracking-wider">AI Diagnosis</span>
      </div>

      {/* Quick status summary (always on) */}
      {quickAnalysis && (
        <div className={`rounded-lg p-3 ${severityColors[quickAnalysis.severity].bg} border ${severityColors[quickAnalysis.severity].border}`}>
          <div className="flex items-center gap-2 mb-2">
            {quickAnalysis.severity === "ok" ? (
              <CheckCircle size={13} className={severityColors[quickAnalysis.severity].text} />
            ) : (
              <AlertTriangle size={13} className={severityColors[quickAnalysis.severity].text} />
            )}
            <span className={`text-xs font-medium ${severityColors[quickAnalysis.severity].text}`}>
              {severityColors[quickAnalysis.severity].label}
            </span>
          </div>
          {quickAnalysis.issues.length > 0 ? (
            <div className="space-y-1">
              {quickAnalysis.issues.map((issue, i) => (
                <div key={i} className="text-[11px] text-gray-400 flex items-start gap-1.5">
                  <span className="text-gray-600 shrink-0">-</span>
                  {issue}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-[11px] text-gray-500">All systems operating normally</div>
          )}
        </div>
      )}

      {/* Deep diagnosis button */}
      <button
        onClick={runDiagnosis}
        disabled={diagnosing}
        className="flex items-center justify-center gap-1.5 py-2 rounded-lg bg-indigo-500/10 hover:bg-indigo-500/20 text-indigo-400 text-xs font-medium border border-indigo-500/20 disabled:opacity-50 transition-colors"
      >
        {diagnosing ? (
          <RefreshCw size={12} className="animate-spin" />
        ) : (
          <Search size={12} />
        )}
        {diagnosing ? "Analyzing..." : "Run Deep Diagnosis"}
      </button>

      {/* Deep diagnosis result */}
      {diagnosis && (
        <div className="rounded-lg bg-card/50 border border-border/30 overflow-hidden">
          {/* Likely issue */}
          {diagnosis.likely_issue && (
            <div className="px-3 py-2.5 border-b border-border/20">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Likely Issue</div>
              <div className="text-sm text-gray-200 font-medium">{diagnosis.likely_issue}</div>
            </div>
          )}

          {/* Explanation */}
          {diagnosis.explanation && (
            <div className="px-3 py-2.5 border-b border-border/20">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Why</div>
              <div className="text-[11px] text-gray-400 leading-relaxed">{diagnosis.explanation}</div>
            </div>
          )}

          {/* Analysis fallback (if no structured fields) */}
          {!diagnosis.likely_issue && diagnosis.analysis && (
            <div className="px-3 py-2.5 border-b border-border/20">
              <div className="text-[11px] text-gray-400 leading-relaxed whitespace-pre-wrap">
                {diagnosis.analysis}
              </div>
            </div>
          )}

          {/* Suggested actions */}
          {diagnosis.actions && diagnosis.actions.length > 0 && (
            <div className="px-3 py-2.5 border-b border-border/20">
              <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-1.5">Suggested Actions</div>
              <div className="space-y-1.5">
                {diagnosis.actions.map((action, i) => (
                  <div key={i} className="flex items-start gap-2 text-[11px] text-gray-400">
                    <Zap size={10} className="text-accent shrink-0 mt-0.5" />
                    <span className="flex-1">{action.label}</span>
                    {action.auto_fixable && (
                      <span className="text-[9px] px-1 py-0.5 rounded bg-accent/20 text-accent shrink-0">auto</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Confidence */}
          {diagnosis.confidence != null && (
            <div className="px-3 py-2 flex items-center gap-2">
              <span className="text-[10px] text-gray-500">Confidence</span>
              <div className="flex-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full bg-indigo-500"
                  style={{ width: `${Math.round(diagnosis.confidence * 100)}%` }}
                />
              </div>
              <span className="text-[10px] text-gray-400 font-mono">
                {(diagnosis.confidence * 100).toFixed(0)}%
              </span>
            </div>
          )}

          <button
            onClick={dismissDiagnosis}
            className="w-full py-1.5 text-[10px] text-gray-600 hover:text-gray-400 transition-colors"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Autopilot section */}
      {autopilot && (
        <div className="rounded-lg bg-card/40 border border-border/30 p-3 space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-gray-500 uppercase tracking-wider">Autopilot</span>
            <button
              onClick={doToggleAutopilot}
              className={`px-2 py-0.5 rounded text-[10px] font-medium transition-colors ${
                autopilot.enabled
                  ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/25"
                  : "bg-gray-500/15 text-gray-400 border border-gray-500/25"
              }`}
            >
              {autopilot.enabled ? "ON" : "OFF"}
            </button>
          </div>
          <div className="text-[10px] text-gray-600 font-mono">{autopilot.actions_taken} actions taken</div>

          {/* Pending veto actions */}
          {pendingActions.length > 0 && (
            <div className="space-y-1.5 pt-1">
              <div className="text-[10px] text-amber-400 font-medium">
                {pendingActions.length} pending action{pendingActions.length > 1 ? "s" : ""}
              </div>
              {pendingActions.map(a => (
                <div key={a.action_id} className="rounded-md bg-amber-500/5 border border-amber-500/15 p-2 space-y-1.5">
                  <div className="text-[11px] text-gray-300">{a.action}</div>
                  <div className="text-[10px] text-gray-500">{a.reason}</div>
                  <div className="flex items-center gap-2">
                    <div className="flex-1 h-1 bg-gray-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-amber-500 rounded-full transition-all"
                        style={{ width: `${Math.max(0, (a.veto_remaining_seconds / 300) * 100)}%` }}
                      />
                    </div>
                    <span className="text-[9px] text-gray-600 font-mono">{Math.ceil(a.veto_remaining_seconds)}s</span>
                    <button
                      onClick={() => doVeto(a.action_id)}
                      className="px-1.5 py-0.5 rounded bg-red-500/15 hover:bg-red-500/25 text-red-400 text-[9px] font-medium border border-red-500/20"
                    >
                      Veto
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Key metrics summary */}
      {snapshot && (
        <div className="rounded-lg bg-card/40 border border-border/30 p-3">
          <div className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">System Metrics</div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { label: "Queue", value: snapshot.queue_depth, color: snapshot.queue_depth > 20 ? "#f59e0b" : "#22c55e" },
              { label: "GPU", value: snapshot.gpu_pct != null ? `${snapshot.gpu_pct}%` : "—", color: (snapshot.gpu_pct ?? 0) > 90 ? "#ef4444" : "#f59e0b" },
              { label: "Workers", value: `${snapshot.gpu_workers_ready}/${snapshot.gpu_workers_alive}`, color: "#8b5cf6" },
              { label: "Throughput", value: `${snapshot.throughput_jobs_per_min}/min`, color: "#22c55e" },
            ].map(m => (
              <div key={m.label} className="text-center">
                <div className="text-xs font-mono font-bold" style={{ color: m.color }}>{m.value}</div>
                <div className="text-[9px] text-gray-600">{m.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
